import { useState, useEffect, type ComponentType } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  User,
  Globe,
  BarChart3,
  Instagram,
  Youtube,
  ChevronRight,
  Calendar,
  ImagePlus,
  Link2,
  Music2,
  MessageCircle,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import FileUploader from '../../components/ui/FileUploader';
import TagSelector from '../../components/ui/TagSelector';
import { fromSlug, kolUrlSegment } from '../../lib/slug';
import RateCardManager from '../../components/RateCardManager';
import Modal from '../../components/ui/Modal';
import type { KolGender } from '../../types';
import { rememberKolProfileForInstagramOAuth } from './InstagramCallback';
import { getPublicApiBaseUrl } from '../../lib/publicApiBase';
import { resolveMediaUrl } from '../../lib/mediaUrl';

/** Facebook “f” mark (lucide has no official brand glyph). */
function FacebookGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    </svg>
  );
}

type LinkPlatformDef = {
  id: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  cardClass: string;
  iconClass: string;
};

const LINK_PLATFORMS: LinkPlatformDef[] = [
  {
    id: 'instagram',
    label: 'Instagram',
    Icon: Instagram,
    cardClass:
      'bg-gradient-to-br from-fuchsia-500/10 via-pink-500/10 to-amber-500/10 ring-1 ring-pink-200/60 hover:ring-pink-300/80',
    iconClass: 'text-pink-600 drop-shadow-sm',
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    Icon: Music2,
    cardClass:
      'bg-gradient-to-br from-slate-800 to-slate-950 ring-1 ring-slate-700/80 hover:ring-slate-500/90',
    iconClass: 'text-white',
  },
  {
    id: 'youtube',
    label: 'YouTube',
    Icon: Youtube,
    cardClass:
      'bg-gradient-to-br from-red-500/10 to-rose-600/10 ring-1 ring-red-200/70 hover:ring-red-300/90',
    iconClass: 'text-red-600',
  },
  {
    id: 'facebook',
    label: 'Facebook',
    Icon: FacebookGlyph,
    cardClass:
      'bg-gradient-to-br from-blue-500/10 to-indigo-600/10 ring-1 ring-blue-200/70 hover:ring-blue-300/90',
    iconClass: 'text-blue-600',
  },
  {
    id: 'wechat',
    label: 'WeChat',
    Icon: MessageCircle,
    cardClass:
      'bg-gradient-to-br from-emerald-500/10 to-green-600/10 ring-1 ring-emerald-200/70 hover:ring-emerald-300/90',
    iconClass: 'text-emerald-600',
  },
];

const STEPS = [
  { n: 1, title: 'You' },
  { n: 2, title: 'Platforms' },
  { n: 3, title: 'Discoverability' },
  { n: 4, title: 'Audience' },
  { n: 5, title: 'Availability' },
  { n: 6, title: 'Portfolio' },
  { n: 7, title: 'Rate cards' },
];

function parseList(s: string): string[] {
  return s
    .split(/[,;\n]/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export default function KOLProfileEdit() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: rawKolRouteSegment } = useParams();
  const routeParamId = rawKolRouteSegment ? fromSlug(rawKolRouteSegment) : undefined;
  const { user } = useAuthStore();
  const isAgencyMode = !!routeParamId;

  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [instagramConnectEnabled, setInstagramConnectEnabled] = useState(false);
  const [igConsentChecked, setIgConsentChecked] = useState(false);
  const [igConnecting, setIgConnecting] = useState(false);
  const [bestPostUrl, setBestPostUrl] = useState('');
  const [bestPostCaption, setBestPostCaption] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [gender, setGender] = useState<KolGender | ''>('');
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [bio, setBio] = useState('');

  const [instagramHandle, setInstagramHandle] = useState('');
  const [tiktokHandle, setTiktokHandle] = useState('');
  const [youtubeChannel, setYoutubeChannel] = useState('');
  const [wechatId, setWechatId] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');

  const [igFollowers, setIgFollowers] = useState('');
  const [igEngagement, setIgEngagement] = useState('');
  const [tiktokFollowers, setTiktokFollowers] = useState('');
  const [tiktokEngagement, setTiktokEngagement] = useState('');
  const [ytFollowers, setYtFollowers] = useState('');
  const [ytEngagement, setYtEngagement] = useState('');
  const [fbFollowers, setFbFollowers] = useState('');
  const [fbEngagement, setFbEngagement] = useState('');
  const [wechatFollowers, setWechatFollowers] = useState('');

  const [specialtiesStr, setSpecialtiesStr] = useState('');
  const [languagesStr, setLanguagesStr] = useState('');
  const [locationCity, setLocationCity] = useState('');
  const [ethnicity, setEthnicity] = useState('');
  const [avgPostRate, setAvgPostRate] = useState('');
  const [avgVideoRate, setAvgVideoRate] = useState('');
  const [avgStoryRate, setAvgStoryRate] = useState('');
  const [bankDetails, setBankDetails] = useState('');
  const [icPassport, setIcPassport] = useState('');

  const [audienceMalePct, setAudienceMalePct] = useState('');
  const [audienceFemalePct, setAudienceFemalePct] = useState('');
  const [audienceMajorityRace, setAudienceMajorityRace] = useState('');
  const [audienceAgeGroup, setAudienceAgeGroup] = useState('');

  const [availStart, setAvailStart] = useState('');
  const [availEnd, setAvailEnd] = useState('');
  const [availLabel, setAvailLabel] = useState('');
  const [availabilityRows, setAvailabilityRows] = useState<any[]>([]);
  const [portfolioItems, setPortfolioItems] = useState<any[]>([]);

  const [tags, setTags] = useState<{id: string; name: string}[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  const [rateCards, setRateCards] = useState<any[]>([]);
  const [urlSlugEdit, setUrlSlugEdit] = useState('');
  const [extraContactEmails, setExtraContactEmails] = useState('');
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [relinkOpen, setRelinkOpen] = useState(false);
  const [relinkEmail, setRelinkEmail] = useState('');
  const [relinkBusy, setRelinkBusy] = useState(false);
  const [linkModalPlatform, setLinkModalPlatform] = useState<string | null>(null);
  const resolvedKolId = routeParamId || user?.kolProfile?.id;
  const igLinked = !!(profile?.instagramConnection?.instagramUserId);

  useEffect(() => {
    if (resolvedKolId) {
      loadProfile(resolvedKolId);
      loadTags();
    } else {
      setLoading(false);
    }
  }, [resolvedKolId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/config/public');
        setInstagramConnectEnabled(!!res.data?.instagramKolConnect?.enabled);
      } catch {
        setInstagramConnectEnabled(false);
      }
    })();
  }, []);

  useEffect(() => {
    const ig = searchParams.get('instagram');
    const stepParam = searchParams.get('step');
    if (ig === 'connected') {
      setStep(2);
      setIgConsentChecked(true);
    }
    if (stepParam) {
      const n = parseInt(stepParam, 10);
      if (Number.isFinite(n)) setStep(Math.min(Math.max(1, n), STEPS.length));
    }
    if (ig === 'connected' || stepParam) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadTags = async () => {
    try {
      const response = await api.get('/tags');
      setTags(response.data);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const loadProfile = async (profileId: string) => {
    try {
      const response = await api.get(`/kols/${profileId}`);
      const p = response.data;
      setProfile(p);
      setUrlSlugEdit(p.urlSlug ?? '');
      setName(p.name || '');
      setEmail(p.email || '');
      const extras = Array.isArray(p.contactEmails) ? p.contactEmails.map((x: { email: string }) => x.email).filter(Boolean) : [];
      setExtraContactEmails(extras.join('\n'));
      setPhone(p.phone || '');
      setGender((p.gender as KolGender) || '');
      setProfileImageUrl(p.profileImageUrl || '');
      setNotes(p.notes || '');
      setBio(p.bio || '');
      setInstagramHandle(p.instagramHandle || '');
      setTiktokHandle(p.tiktokHandle || '');
      setYoutubeChannel(p.youtubeChannel || '');
      setWechatId(p.wechatId || '');
      setWhatsappNumber(p.whatsappNumber || '');
      const specs = p.contentSpecialties;
      setSpecialtiesStr(Array.isArray(specs) ? specs.join(', ') : '');
      const langs = p.languages;
      setLanguagesStr(Array.isArray(langs) ? langs.join(', ') : '');
      setLocationCity(p.locationCity || '');
      setEthnicity(p.ethnicity || '');
      setAvgPostRate(p.avgPostRate != null ? String(p.avgPostRate) : '');
      setAvgVideoRate(p.avgVideoRate != null ? String(p.avgVideoRate) : '');
      setAvgStoryRate(p.avgStoryRate != null ? String(p.avgStoryRate) : '');
      setBankDetails(p.bankDetails || '');
      setIcPassport(p.icPassport || '');

      const platforms = p.platforms || {};
      if (platforms.instagram) {
        setIgFollowers(
          p.instagramFollowers != null ? String(p.instagramFollowers) : platforms.instagram.followers?.toString() || ''
        );
        setIgEngagement(platforms.instagram.engagementRate?.toString() || '');
      } else if (p.instagramFollowers != null) {
        setIgFollowers(String(p.instagramFollowers));
      }
      if (platforms.tiktok) {
        setTiktokFollowers(
          p.tiktokFollowers != null ? String(p.tiktokFollowers) : platforms.tiktok.followers?.toString() || ''
        );
        setTiktokEngagement(platforms.tiktok.engagementRate?.toString() || '');
      } else if (p.tiktokFollowers != null) {
        setTiktokFollowers(String(p.tiktokFollowers));
      }
      if (platforms.youtube) {
        setYtFollowers(
          p.youtubeFollowers != null ? String(p.youtubeFollowers) : platforms.youtube.followers?.toString() || ''
        );
        setYtEngagement(platforms.youtube.engagementRate?.toString() || '');
      } else if (p.youtubeFollowers != null) {
        setYtFollowers(String(p.youtubeFollowers));
      }
      if (platforms.facebook) {
        setFbFollowers(
          p.facebookFollowers != null ? String(p.facebookFollowers) : platforms.facebook.followers?.toString() || ''
        );
        const fbE = platforms.facebook.engagement ?? platforms.facebook.engagementRate;
        setFbEngagement(fbE != null && fbE !== '' ? String(fbE) : '');
      } else if (p.facebookFollowers != null) {
        setFbFollowers(String(p.facebookFollowers));
      }
      if (platforms.wechat) {
        setWechatFollowers(
          p.wechatFollowers != null ? String(p.wechatFollowers) : (platforms.wechat as { followers?: number }).followers?.toString() || ''
        );
      } else if (p.wechatFollowers != null) {
        setWechatFollowers(String(p.wechatFollowers));
      }

      const audience = p.audience || {};
      setAudienceMalePct(audience.sexPercentage?.male?.toString() || '');
      setAudienceFemalePct(audience.sexPercentage?.female?.toString() || '');
      setAudienceMajorityRace(audience.majorityRace || '');
      setAudienceAgeGroup(audience.ageGroup || '');
      setAvailabilityRows(p.availability || []);
      setSelectedTagIds(p.tags?.map((t: any) => t.tagId) || []);
      setRateCards(p.rateCards || []);
      const portfolioRes = await api.get('/kols/' + profileId + '/portfolio').catch(() => ({ data: [] }));
      setPortfolioItems(portfolioRes.data || []);
      const perf = p.pastPerformance;
      if (perf && typeof perf === 'object' && perf !== null) {
        const bp = (perf as { bestPost?: { url?: string; caption?: string } }).bestPost;
        if (bp) {
          setBestPostUrl(bp.url || '');
          setBestPostCaption(bp.caption || '');
        } else {
          setBestPostUrl('');
          setBestPostCaption('');
        }
      }
    } catch (error) {
      console.error('Failed to load KOL profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => {
    const optionalInt = (s: string): number | null => {
      const t = s.trim();
      if (t === '') return null;
      const n = parseInt(t, 10);
      return Number.isFinite(n) ? n : null;
    };

    const prevPlatforms = (profile?.platforms || {}) as Record<string, any>;

    const platforms: any = {};
    if (instagramHandle || igFollowers || igEngagement || prevPlatforms.instagram || profile?.instagramProfileUrl) {
      const prevIg =
        prevPlatforms.instagram && typeof prevPlatforms.instagram === 'object'
          ? prevPlatforms.instagram
          : {};
      platforms.instagram = {
        ...prevIg,
        handle: instagramHandle,
        followers: parseInt(igFollowers, 10) || Number((prevIg as { followers?: number }).followers) || 0,
        engagementRate:
          parseFloat(igEngagement) ||
          Number((prevIg as { engagementRate?: number }).engagementRate) ||
          0,
      };
      const igUrl = (prevIg as { url?: string }).url ?? profile?.instagramProfileUrl;
      if (igUrl) platforms.instagram.url = igUrl;
    }
    if (tiktokHandle || tiktokFollowers || tiktokEngagement || prevPlatforms.tiktok || profile?.tiktokProfileUrl) {
      const prevTt =
        prevPlatforms.tiktok && typeof prevPlatforms.tiktok === 'object' ? prevPlatforms.tiktok : {};
      platforms.tiktok = {
        ...prevTt,
        handle: tiktokHandle,
        followers: parseInt(tiktokFollowers, 10) || Number((prevTt as { followers?: number }).followers) || 0,
        engagementRate:
          parseFloat(tiktokEngagement) ||
          Number((prevTt as { engagementRate?: number }).engagementRate) ||
          0,
      };
      const ttUrl = (prevTt as { url?: string }).url ?? profile?.tiktokProfileUrl;
      if (ttUrl) platforms.tiktok.url = ttUrl;
    }
    if (youtubeChannel || ytFollowers || ytEngagement || prevPlatforms.youtube || profile?.youtubeChannelUrl) {
      const prevYt =
        prevPlatforms.youtube && typeof prevPlatforms.youtube === 'object' ? prevPlatforms.youtube : {};
      platforms.youtube = {
        ...prevYt,
        handle: youtubeChannel,
        followers: parseInt(ytFollowers, 10) || Number((prevYt as { followers?: number }).followers) || 0,
        engagementRate:
          parseFloat(ytEngagement) ||
          Number((prevYt as { engagementRate?: number }).engagementRate) ||
          0,
      };
      const ytUrl = (prevYt as { url?: string }).url ?? profile?.youtubeChannelUrl;
      if (ytUrl) platforms.youtube.url = ytUrl;
    }
    if (
      prevPlatforms.facebook?.handle ||
      profile?.facebookHandle ||
      fbFollowers ||
      fbEngagement ||
      prevPlatforms.facebook ||
      profile?.facebookProfileUrl
    ) {
      const prevFb =
        prevPlatforms.facebook && typeof prevPlatforms.facebook === 'object' ? prevPlatforms.facebook : {};
      platforms.facebook = {
        ...prevFb,
        handle: profile?.facebookHandle || (prevFb as { handle?: string }).handle || '',
        followers: parseInt(fbFollowers, 10) || Number((prevFb as { followers?: number }).followers) || 0,
        engagement:
          parseFloat(fbEngagement) ||
          Number((prevFb as { engagement?: number }).engagement) ||
          Number((prevFb as { engagementRate?: number }).engagementRate) ||
          0,
      };
      const fbUrl = (prevFb as { url?: string }).url ?? profile?.facebookProfileUrl;
      if (fbUrl) platforms.facebook.url = fbUrl;
    }
    if (wechatId || wechatFollowers || prevPlatforms.wechat || profile?.wechatProfileUrl) {
      const prevWx =
        typeof prevPlatforms.wechat === 'object' && prevPlatforms.wechat ? prevPlatforms.wechat : {};
      platforms.wechat = {
        ...prevWx,
        followers: parseInt(wechatFollowers, 10) || Number((prevWx as { followers?: number }).followers) || 0,
      };
      const wxUrl = (prevWx as { url?: string }).url ?? profile?.wechatProfileUrl;
      if (wxUrl) platforms.wechat.url = wxUrl;
    }

    const audience: any = {};
    if (audienceMalePct || audienceFemalePct) {
      audience.sexPercentage = {
        male: parseFloat(audienceMalePct) || 0,
        female: parseFloat(audienceFemalePct) || 0,
      };
    }
    if (audienceMajorityRace) audience.majorityRace = audienceMajorityRace;
    if (audienceAgeGroup) audience.ageGroup = audienceAgeGroup;

    const specialties = parseList(specialtiesStr);
    const languages = parseList(languagesStr);

    const basePerf =
      profile?.pastPerformance && typeof profile.pastPerformance === 'object' && profile.pastPerformance !== null
        ? ({ ...(profile.pastPerformance as Record<string, unknown>) })
        : {};
    if (bestPostUrl.trim() || bestPostCaption.trim()) {
      basePerf.bestPost = {
        url: bestPostUrl.trim(),
        caption: bestPostCaption.trim(),
        updatedAt: new Date().toISOString(),
      };
    } else {
      delete basePerf.bestPost;
    }

    return {
      name,
      email,
      phone,
      ...(isAgencyMode ? { urlSlug: urlSlugEdit.trim() === '' ? null : urlSlugEdit.trim() } : {}),
      ...(isAgencyMode
        ? {
            contactEmails: parseList(extraContactEmails)
              .map((e) => e.trim())
              .filter((e) => e.includes('@')),
          }
        : {}),
      gender: gender || null,
      profileImageUrl,
      notes,
      bio,
      instagramHandle: instagramHandle || undefined,
      instagramProfileUrl: profile?.instagramProfileUrl || undefined,
      tiktokHandle: tiktokHandle || undefined,
      tiktokProfileUrl: profile?.tiktokProfileUrl || undefined,
      youtubeChannel: youtubeChannel || undefined,
      youtubeChannelUrl: profile?.youtubeChannelUrl || undefined,
      facebookHandle: profile?.facebookHandle || undefined,
      facebookProfileUrl: profile?.facebookProfileUrl || undefined,
      wechatId: wechatId || undefined,
      wechatProfileUrl: profile?.wechatProfileUrl || undefined,
      whatsappNumber: whatsappNumber || undefined,
      contentSpecialties: specialties.length ? specialties : undefined,
      languages: languages.length ? languages : undefined,
      locationCity: locationCity || undefined,
      ethnicity: ethnicity || undefined,
      avgPostRate: avgPostRate === '' ? undefined : parseInt(avgPostRate, 10),
      avgVideoRate: avgVideoRate === '' ? undefined : parseInt(avgVideoRate, 10),
      avgStoryRate: avgStoryRate === '' ? undefined : parseInt(avgStoryRate, 10),
      bankDetails: bankDetails || undefined,
      icPassport: icPassport || undefined,
      instagramFollowers: optionalInt(igFollowers),
      tiktokFollowers: optionalInt(tiktokFollowers),
      youtubeFollowers: optionalInt(ytFollowers),
      facebookFollowers: optionalInt(fbFollowers),
      wechatFollowers: optionalInt(wechatFollowers),
      platforms,
      audience,
      tagIds: selectedTagIds,
      pastPerformance: basePerf,
    };
  };

  const handleRelink = async () => {
    if (!profile?.id) return;
    const em = relinkEmail.trim();
    if (!em) {
      alert('Enter the email the creator uses to sign in to myKOL.');
      return;
    }
    try {
      setRelinkBusy(true);
      await api.post(`/kols/${kolUrlSegment(profile)}/relink`, { userEmail: em });
      setRelinkOpen(false);
      setRelinkEmail('');
      alert('KOL profile linked to that user account.');
      await loadProfile(profile.id);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Relink failed');
    } finally {
      setRelinkBusy(false);
    }
  };

  const handleSave = async () => {
    if (!profile) return;
    try {
      setSaving(true);
      await api.put(`/kols/${kolUrlSegment(profile)}`, buildPayload());
      alert('Profile updated successfully!');
      await loadProfile(profile.id);
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      alert(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const startInstagramOAuth = async () => {
    if (!profile?.id || !igConsentChecked) return;
    setIgConnecting(true);
    try {
      const { data } = await api.get('/kols/me/instagram/connect-url', {
        params: { kolProfileId: profile.id },
      });
      rememberKolProfileForInstagramOAuth(profile.id);
      window.location.href = data.url as string;
    } catch (error: any) {
      alert(error.response?.data?.message || error.response?.data?.error || 'Could not start Instagram login');
      setIgConnecting(false);
    }
  };

  const syncInstagram = async () => {
    if (!profile?.id) return;
    setIgConnecting(true);
    try {
      await api.post('/kols/me/instagram/sync', { kolProfileId: profile.id });
      await loadProfile(profile.id);
      alert('Instagram stats refreshed from Meta.');
    } catch (error: any) {
      alert(error.response?.data?.message || error.response?.data?.error || 'Sync failed');
    } finally {
      setIgConnecting(false);
    }
  };

  /**
   * Withdraws the Instagram grant.
   *
   * Confirmed first, and the confirmation says what does and does not happen: the stored access is
   * deleted and syncing stops, but the follower counts already on the agency's roster stay. Those
   * were gathered with consent at the time and are the agency's own record of a creator they work
   * with; removing data from the shared pool is a different request with its own route.
   */
  const disconnectInstagram = async () => {
    if (!profile?.id) return;
    const ok = window.confirm(
      'Disconnect Instagram?\n\n' +
        'kol.my will delete its stored access and stop syncing your account. Follower counts already ' +
        'saved by your agency stay on their records.'
    );
    if (!ok) return;

    setIgConnecting(true);
    try {
      await api.delete('/kols/me/instagram', { params: { kolProfileId: profile.id } });
      await loadProfile(profile.id);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Could not disconnect. Please try again.');
    } finally {
      setIgConnecting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12">Loading...</div>;
  }

  if (!loading && !resolvedKolId && user?.role === 'KOL') {
    return (
      <div className="text-center py-12 max-w-lg mx-auto px-4">
        <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 font-medium">No KOL profile linked to your account</p>
        <p className="text-sm text-gray-500 mt-2">
          Invitations are matched by email. Sign in with the same address your agency used on the invite, or ask them to add
          your signup email as an extra contact on your roster profile—then sign out and sign in again.
        </p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 font-medium">No KOL profile found</p>
        <p className="text-sm text-gray-500 mt-2">
          {resolvedKolId
            ? 'Could not load this profile, or you do not have access.'
            : 'Your KOL profile is managed by your agency. Contact them to set it up.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate(isAgencyMode ? `/agency/kols/${kolUrlSegment(profile)}` : '/kol/dashboard')
            }
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title">{isAgencyMode ? 'Edit KOL Profile' : 'My KOL Profile'}</h1>
            <p className="text-gray-600 mt-1">Step {step} of {STEPS.length}: {STEPS[step - 1].title}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAgencyMode && (
            <Button type="button" variant="outline" onClick={() => setRelinkOpen(true)} className="flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Re-link account
            </Button>
          )}
          {!isAgencyMode && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setLinkModalOpen(true)}
              className="flex items-center gap-2"
            >
              <Link2 className="w-4 h-4" />
              Link
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="flex items-center gap-2">
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {STEPS.map((s) => (
          <button
            key={s.n}
            type="button"
            onClick={() => setStep(s.n)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1 ${
              step === s.n ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {s.n}
            <ChevronRight className="w-3 h-3 opacity-60" />
            {s.title}
          </button>
        ))}
      </div>

      {!isAgencyMode && (
        <div className="rounded-2xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/80 p-5 shadow-sm dark:border-emerald-900/40 dark:from-emerald-950/30 dark:via-glass-2 dark:to-teal-950/20">
          <div className="flex items-start gap-3">
            <Sparkles className="w-6 h-6 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-semibold text-gray-900 dark:text-glass-text">Stand out to brands & agencies</h2>
              <p className="text-sm text-gray-600 dark:text-glass-text-2 mt-1 leading-relaxed">
                Complete each step below: richer profiles rank better in search, get clearer briefs, and win more paid
                work. Honest rate cards and a strong portfolio reduce back-and-forth so deals close faster.
              </p>
              <p className="text-xs text-gray-500 dark:text-glass-text-2 mt-2">
                Free tool:{' '}
                <a href="/kol/tax" className="text-emerald-700 dark:text-accent2 underline font-medium">
                  Malaysia tax helper
                </a>{' '}
                (worksheet for MyTax — not tax advice).
              </p>
            </div>
          </div>
        </div>
      )}

      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              About you
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                {profileImageUrl ? (
                  <img src={resolveMediaUrl(profileImageUrl)} alt={name} className="w-24 h-24 rounded-full object-cover border-4 border-gray-100" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center text-white text-3xl font-bold">
                    {name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Profile picture</h3>
                <FileUploader onUpload={(urls) => urls[0] && setProfileImageUrl(urls[0])} accept="image/*" maxSize={5} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your display name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact email</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Contact email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+60 12-345 6789" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={gender}
                  onChange={(e) => setGender((e.target.value as KolGender) || '')}
                >
                  <option value="">Not specified</option>
                  <option value="FEMALE">Female</option>
                  <option value="MALE">Male</option>
                  <option value="NON_BINARY">Non-binary</option>
                  <option value="OTHER">Other</option>
                  <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
                </select>
              </div>
              {isAgencyMode && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profile URL slug <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <Input
                    value={urlSlugEdit}
                    onChange={(e) => setUrlSlugEdit(e.target.value)}
                    placeholder="e.g. jane-doe (leave empty to auto-generate from name)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Public path: /agency/kols/&lt;slug&gt;. Clear the field to remove the custom slug.
                  </p>
                </div>
              )}
              {isAgencyMode && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Extra contact emails <span className="text-gray-400 font-normal">(account linking)</span>
                  </label>
                  <Textarea
                    value={extraContactEmails}
                    onChange={(e) => setExtraContactEmails(e.target.value)}
                    placeholder="One email per line — add the address the creator actually uses to sign in if it differs from the main contact email."
                    rows={3}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Used to auto-link this roster row to their myKOL login after they sign up. Save, then ask them to sign out
                    and sign in again (or use Re-link account).
                  </p>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
              <Textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="What you create, who you reach, and the kinds of partnerships you want…" rows={4} />
              <p className="text-xs text-gray-500 mt-1">A clear bio helps matching — mention niches, regions you cover, and languages.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Internal notes</label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes visible to you and your agency..." rows={2} />
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Platforms & metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-sm text-gray-600 dark:text-glass-text-2">
              Add handles first. For Instagram, you can paste numbers manually or use{' '}
              <strong className="text-gray-800 dark:text-glass-text">Connect Instagram</strong> so we pull follower counts
              and account insights through Meta&apos;s official API (Business / Creator account required).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Instagram handle</label>
                <Input value={instagramHandle} onChange={(e) => setInstagramHandle(e.target.value)} placeholder="@username" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">TikTok handle</label>
                <Input value={tiktokHandle} onChange={(e) => setTiktokHandle(e.target.value)} placeholder="@username" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">YouTube channel</label>
                <Input value={youtubeChannel} onChange={(e) => setYoutubeChannel(e.target.value)} placeholder="Channel name or URL" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">WeChat ID</label>
                <Input value={wechatId} onChange={(e) => setWechatId(e.target.value)} placeholder="WeChat ID" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">WhatsApp</label>
                <Input value={whatsappNumber} onChange={(e) => setWhatsappNumber(e.target.value)} placeholder="+60..." />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Instagram className="w-5 h-5 text-pink-500" />
                <h3 className="font-medium text-gray-900">Instagram metrics</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Followers</label>
                  <Input type="number" value={igFollowers} onChange={(e) => setIgFollowers(e.target.value)} placeholder="10000" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Engagement rate (%)</label>
                  <Input type="number" step="0.01" value={igEngagement} onChange={(e) => setIgEngagement(e.target.value)} placeholder="3.5" />
                </div>
              </div>
              {!isAgencyMode && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!instagramConnectEnabled || igConnecting}
                    onClick={() => {
                      setLinkModalPlatform('instagram');
                      setLinkModalOpen(true);
                    }}
                    className="gap-1"
                  >
                    <Link2 className="w-3.5 h-3.5" />
                    Connect Instagram (Meta API)
                  </Button>
                  {igLinked && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={igConnecting}
                      onClick={syncInstagram}
                      className="gap-1"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${igConnecting ? 'animate-spin' : ''}`} />
                      Refresh from Instagram
                    </Button>
                  )}
                  {igLinked && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={igConnecting}
                      onClick={disconnectInstagram}
                      className="text-red-600 hover:text-red-700"
                    >
                      Disconnect
                    </Button>
                  )}
                  {igLinked && profile?.instagramConnection?.lastSyncedAt && (
                    <span className="text-xs text-gray-500">
                      Last synced: {new Date(profile.instagramConnection.lastSyncedAt).toLocaleString()}
                    </span>
                  )}
                  {!instagramConnectEnabled && (
                    <span className="text-xs text-amber-700">Instagram API not configured on this server.</span>
                  )}
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="secondary" className="text-xs">TikTok</Badge>
                <h3 className="font-medium text-gray-900">TikTok metrics</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Followers</label>
                  <Input type="number" value={tiktokFollowers} onChange={(e) => setTiktokFollowers(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Engagement rate (%)</label>
                  <Input type="number" step="0.01" value={tiktokEngagement} onChange={(e) => setTiktokEngagement(e.target.value)} />
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Youtube className="w-5 h-5 text-red-500" />
                <h3 className="font-medium text-gray-900">YouTube metrics</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Subscribers</label>
                  <Input type="number" value={ytFollowers} onChange={(e) => setYtFollowers(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Engagement rate (%)</label>
                  <Input type="number" step="0.01" value={ytEngagement} onChange={(e) => setYtEngagement(e.target.value)} />
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="secondary" className="text-xs">Facebook</Badge>
                <h3 className="font-medium text-gray-900">Facebook metrics</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Page followers</label>
                  <Input type="number" value={fbFollowers} onChange={(e) => setFbFollowers(e.target.value)} placeholder="0" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Engagement rate (%)</label>
                  <Input type="number" step="0.01" value={fbEngagement} onChange={(e) => setFbEngagement(e.target.value)} />
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="secondary" className="text-xs">WeChat</Badge>
                <h3 className="font-medium text-gray-900">WeChat metrics</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Followers / subscribers</label>
                  <Input type="number" value={wechatFollowers} onChange={(e) => setWechatFollowers(e.target.value)} placeholder="0" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              Discoverability
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Tags and clear rates help brands filter you in search. Agencies use this to shortlist faster — incomplete
              profiles are often skipped.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content categories (tags)</label>
              <TagSelector tags={tags} selectedTagIds={selectedTagIds} onChange={setSelectedTagIds} label="" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content specialties (comma-separated)</label>
              <Input value={specialtiesStr} onChange={(e) => setSpecialtiesStr(e.target.value)} placeholder="Beauty, Tech, F&B" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Languages (comma-separated)</label>
              <Input value={languagesStr} onChange={(e) => setLanguagesStr(e.target.value)} placeholder="English, Malay, Mandarin" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City / location</label>
                <Input value={locationCity} onChange={(e) => setLocationCity(e.target.value)} placeholder="Kuala Lumpur" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ethnicity (self-described)</label>
                <Input value={ethnicity} onChange={(e) => setEthnicity(e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Avg post rate (RM)</label>
                <Input type="number" value={avgPostRate} onChange={(e) => setAvgPostRate(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Avg video rate (RM)</label>
                <Input type="number" value={avgVideoRate} onChange={(e) => setAvgVideoRate(e.target.value)} />
              </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Avg story rate (RM)</label>
              <Input type="number" value={avgStoryRate} onChange={(e) => setAvgStoryRate(e.target.value)} />
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <ImagePlus className="w-4 h-4" />
                Best post or case study (link)
              </h3>
              <p className="text-xs text-gray-600">
                Paste a public URL to a post or campaign you are proud of. This is optional but helps buyers understand
                your style and results.
              </p>
              <Input
                value={bestPostUrl}
                onChange={(e) => setBestPostUrl(e.target.value)}
                placeholder="https://www.instagram.com/p/… or TikTok / YouTube link"
              />
              <Textarea
                value={bestPostCaption}
                onChange={(e) => setBestPostCaption(e.target.value)}
                placeholder="One line: what made this post work (reach, saves, brand goal…)"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank details (sensitive — only share if comfortable)</label>
              <Textarea value={bankDetails} onChange={(e) => setBankDetails(e.target.value)} rows={3} placeholder="Bank name, account name, account number" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IC / Passport (last 4 chars or reference)</label>
              <Input value={icPassport} onChange={(e) => setIcPassport(e.target.value)} placeholder="Optional identifier" />
            </div>
          </CardContent>
        </Card>
      )}

      {step === 5 && profile && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Availability windows
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Add date ranges when you are open for new campaigns. Agencies can filter by these dates in search.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500">Start</label>
                <Input type="date" value={availStart} onChange={(e) => setAvailStart(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-gray-500">End</label>
                <Input type="date" value={availEnd} onChange={(e) => setAvailEnd(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-gray-500">Label (optional)</label>
                <Input value={availLabel} onChange={(e) => setAvailLabel(e.target.value)} placeholder="e.g. Q2 open" />
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              disabled={!availStart || !availEnd}
              onClick={async () => {
                await api.post(`/kols/${profile.id}/availability`, {
                  startDate: availStart,
                  endDate: availEnd,
                  label: availLabel || undefined,
                });
                setAvailStart('');
                setAvailEnd('');
                setAvailLabel('');
                await loadProfile(profile.id);
              }}
            >
              Add window
            </Button>
            <ul className="text-sm space-y-2 border-t pt-4">
              {availabilityRows.map((a: any) => (
                <li key={a.id} className="flex justify-between items-center">
                  <span>
                    {a.startDate?.slice?.(0, 10)} → {a.endDate?.slice?.(0, 10)}
                    {a.label ? ` · ${a.label}` : ''}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      await api.delete(`/kols/${profile.id}/availability/${a.id}`);
                      await loadProfile(profile.id);
                    }}
                  >
                    Remove
                  </Button>
                </li>
              ))}
              {availabilityRows.length === 0 && <li className="text-gray-500">No availability added yet.</li>}
            </ul>
          </CardContent>
        </Card>
      )}

      {step === 6 && profile && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImagePlus className="w-5 h-5" />
              Portfolio
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-sm text-gray-600">
              Upload images or videos to showcase your work. Add a short caption for each item.
            </p>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Add media</h3>
              <FileUploader
                multiple
                accept="image/*,video/*"
                maxSize={50}
                onUpload={async (urls) => {
                  for (const url of urls) {
                    const isVideo = /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url);
                    const type = isVideo ? 'VIDEO' : 'IMAGE';
                    await api.post(`/kols/${profile.id}/portfolio`, { type, url, title: '' });
                  }
                  const refreshed = await api.get(`/kols/${profile.id}/portfolio`).catch(() => ({ data: [] }));
                  setPortfolioItems(refreshed.data || []);
                }}
              />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {portfolioItems.map((item: any) => (
                <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm flex flex-col">
                  <div className="aspect-square relative bg-gray-100 group">
                    {item.type === 'IMAGE' && (
                      <img src={item.url} alt={item.title || ''} className="w-full h-full object-cover" />
                    )}
                    {item.type === 'VIDEO' && (
                      <video src={item.url} className="w-full h-full object-cover" muted playsInline controls />
                    )}
                    {item.type === 'LINK' && (
                      <div className="w-full h-full flex flex-col items-center justify-center p-3 text-center">
                        <Badge variant="secondary" className="mb-2">
                          Link
                        </Badge>
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 break-all line-clamp-4 hover:underline"
                        >
                          {item.url}
                        </a>
                      </div>
                    )}
                    <div className="absolute top-2 right-2 flex gap-1">
                      {item.type === 'VIDEO' && (
                        <Badge variant="secondary" className="text-[10px]">
                          Video
                        </Badge>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 px-2 bg-white/90 backdrop-blur-sm"
                        onClick={async () => {
                          await api.delete(`/kols/${profile.id}/portfolio/${item.id}`);
                          setPortfolioItems((prev) => prev.filter((x: any) => x.id !== item.id));
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                  <div className="p-2 flex-1 flex flex-col gap-1">
                    <label className="text-xs text-gray-500">Caption</label>
                    <Textarea
                      value={item.caption ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPortfolioItems((prev) => prev.map((x: any) => (x.id === item.id ? { ...x, caption: v } : x)));
                      }}
                      onBlur={async (e) => {
                        try {
                          await api.patch(`/kols/${profile.id}/portfolio/${item.id}`, { caption: e.target.value });
                        } catch (err) {
                          console.error('Failed to save caption:', err);
                        }
                      }}
                      placeholder="Optional caption"
                      rows={2}
                      className="text-sm min-h-[60px]"
                    />
                  </div>
                </div>
              ))}
            </div>
            {portfolioItems.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">No portfolio items yet. Upload images or videos above.</p>
            )}
          </CardContent>
        </Card>
      )}

      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Audience demographics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Male audience (%)</label>
                <Input type="number" min="0" max="100" value={audienceMalePct} onChange={(e) => setAudienceMalePct(e.target.value)} placeholder="45" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Female audience (%)</label>
                <Input type="number" min="0" max="100" value={audienceFemalePct} onChange={(e) => setAudienceFemalePct(e.target.value)} placeholder="55" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Majority ethnicity (audience)</label>
                <select
                  value={audienceMajorityRace}
                  onChange={(e) => setAudienceMajorityRace(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select...</option>
                  <option value="Malay">Malay</option>
                  <option value="Chinese">Chinese</option>
                  <option value="Indian">Indian</option>
                  <option value="Mixed">Mixed / Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary age group</label>
                <select
                  value={audienceAgeGroup}
                  onChange={(e) => setAudienceAgeGroup(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select...</option>
                  <option value="13-17">13-17</option>
                  <option value="18-24">18-24</option>
                  <option value="25-34">25-34</option>
                  <option value="35-44">35-44</option>
                  <option value="45+">45+</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 7 && profile && (
        <Card>
          <CardHeader>
            <CardTitle>Rate cards</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <RateCardManager
              kolId={profile.id}
              rateCards={rateCards}
              onReload={() => loadProfile(profile.id)}
            />
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between pb-8">
        <Button variant="outline" disabled={step <= 1} onClick={() => setStep((s) => Math.max(1, s - 1))}>
          Back
        </Button>
        <div className="flex gap-2">
          {step < STEPS.length && (
            <Button variant="outline" onClick={() => setStep((s) => Math.min(STEPS.length, s + 1))}>
              Next
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} size="lg" className="flex items-center gap-2">
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save all'}
          </Button>
        </div>
      </div>

      <Modal
        isOpen={linkModalOpen}
        onClose={() => {
          setLinkModalOpen(false);
          setLinkModalPlatform(null);
          setIgConsentChecked(false);
        }}
        title={linkModalPlatform === 'instagram' ? 'Connect Instagram' : 'Link a platform'}
        size="md"
      >
        {linkModalPlatform === 'instagram' && !isAgencyMode ? (
          <div className="space-y-4 text-sm text-gray-700">
            <p>
              We use{' '}
              <a
                href="https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login/business-login/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-pink-600 underline"
              >
                Instagram Business Login
              </a>{' '}
              and the{' '}
              <a
                href="https://developers.facebook.com/docs/instagram-platform/insights/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-pink-600 underline"
              >
                Insights API
              </a>{' '}
              only for <strong>your</strong> professional account. We store follower counts and permitted metrics to show
              agencies on kol.my — we do not post on your behalf.
            </p>
            <p className="text-xs text-gray-500">
              Redirect URI in your Meta app must include:{' '}
              <code className="break-all bg-gray-100 px-1 rounded">
                {typeof window !== 'undefined' ? window.location.origin : ''}/kol/instagram-callback
              </code>
              {getPublicApiBaseUrl() && (
                <>
                  {' '}
                  (frontend origin). Backend: set <code className="bg-gray-100 px-1">INSTAGRAM_APP_ID</code> and{' '}
                  <code className="bg-gray-100 px-1">INSTAGRAM_APP_SECRET</code>.
                </>
              )}
            </p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 rounded border-gray-300"
                checked={igConsentChecked}
                onChange={(e) => setIgConsentChecked(e.target.checked)}
              />
              <span>
                I agree to connect my Instagram professional account and for kol.my to read the profile and insights data
                described above, so brands and my agency can evaluate fit. I can disconnect at any time from this page,
                which deletes the stored access and stops all future syncing.
              </span>
            </label>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setLinkModalPlatform(null)}>
                Back
              </Button>
              <Button type="button" disabled={!igConsentChecked || igConnecting} onClick={startInstagramOAuth}>
                {igConnecting ? 'Redirecting…' : 'Continue to Instagram'}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-6 text-center">Choose a platform — Instagram uses the official API above</p>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5 md:gap-3">
              {LINK_PLATFORMS.map(({ id, label, Icon, cardClass, iconClass }) => (
                <button
                  key={id}
                  type="button"
                  aria-label={`Link ${label}`}
                  onClick={() => {
                    if (id === 'instagram' && !isAgencyMode && instagramConnectEnabled) {
                      setLinkModalPlatform('instagram');
                      setIgConsentChecked(false);
                      return;
                    }
                    alert('Feature coming soon — add your handle in the Platforms step for now.');
                    setLinkModalOpen(false);
                  }}
                  className={`group relative flex aspect-square flex-col items-center justify-center rounded-2xl p-5 shadow-sm transition-all duration-200 hover:scale-[1.04] hover:shadow-md active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 ${cardClass}`}
                >
                  <Icon className={`h-11 w-11 transition-transform duration-200 group-hover:scale-110 ${iconClass}`} />
                </button>
              ))}
            </div>
          </>
        )}
      </Modal>

      <Modal
        isOpen={relinkOpen}
        onClose={() => {
          setRelinkOpen(false);
          setRelinkEmail('');
        }}
        title="Re-link to user account"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setRelinkOpen(false)} disabled={relinkBusy}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleRelink()} disabled={relinkBusy}>
              {relinkBusy ? 'Linking…' : 'Link account'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600 mb-3">
          Enter the <strong>exact email</strong> the creator uses to sign in to myKOL (Supabase account). Their role must
          already be KOL.
        </p>
        <label className="block text-sm font-medium text-gray-700 mb-1">Sign-in email</label>
        <Input type="email" value={relinkEmail} onChange={(e) => setRelinkEmail(e.target.value)} placeholder="name@example.com" />
      </Modal>
    </div>
  );
}
